import type { DocRefProcessingStatus } from '@features/patient-artifacts';

/**
 * Fast polling cadence for the Intake Hub document inventory.
 *
 * Document classification runs asynchronously after an upload. The artifact
 * event stream normally refreshes the inventory when it finishes, but a missed
 * event left "Reading this document" on screen until a manual refresh. While
 * a document is still being read, the inventory query polls as a fallback;
 * once every document reaches a terminal state the polling stops.
 *
 * This is the cadence for the first `DOCUMENT_PROCESSING_POLL_FAST_WINDOW_MS`
 * after a document was last touched — the window in which most classifications
 * land, and the one where a stale screen is most noticeable.
 */
export const DOCUMENT_PROCESSING_POLL_INTERVAL_MS = 3000;

/**
 * How long the fast cadence lasts, measured from when the document was last
 * touched. Classification that runs long (a real W-9 took ~75 s, ~25 requests
 * at the flat 3 s cadence) does not need second-by-second checking after the
 * first half minute, so polling widens rather than stopping.
 */
export const DOCUMENT_PROCESSING_POLL_FAST_WINDOW_MS = 30 * 1000;

/**
 * The cadence once the fast window has elapsed, until the age cap below. The
 * same ~75 s classification now costs ~10 fast requests plus ~5 slow ones
 * instead of ~25.
 */
export const DOCUMENT_PROCESSING_POLL_SLOW_INTERVAL_MS = 10 * 1000;

/**
 * How long after a document was last touched it is still worth polling for.
 * A document reference whose enrichment job died without recording `failed`
 * stays `pending` forever; without this cap every viewer of the org would poll
 * for the life of the tab. Matches the pending-upload ghost timeout in
 * `@features/patient-artifacts`.
 */
export const DOCUMENT_PROCESSING_POLL_MAX_AGE_MS = 5 * 60 * 1000;

/**
 * The cadence while the artifact event stream reports connected. The stream
 * refreshes the inventory on every lifecycle event and on every reconnect, so
 * polling is only a safety net for a frame lost in transit (Redis pub/sub does
 * not replay). The fast/slow cadences above apply only while the stream is
 * down. Measured before this existed: 13-23 inventory requests a minute per
 * viewer for the whole 50-110 s a document takes to read.
 */
export const DOCUMENT_PROCESSING_POLL_STREAM_CONNECTED_INTERVAL_MS = 30 * 1000;

const TERMINAL_PROCESSING_STATUSES: ReadonlySet<string> = new Set<
  DocRefProcessingStatus | 'processed'
>(['enriched', 'processed', 'failed']);

export interface PollableDocumentInventoryItem {
  document_reference: {
    processing_status?: string | null;
    source_artifact_id?: string | null;
    created_at?: string | null;
    updated_at?: string | null;
  };
}

export interface PollablePendingUpload {
  artifactId: string;
  status?: string | null;
}

export function isDocumentProcessingComplete(processingStatus?: string | null): boolean {
  return Boolean(processingStatus) && TERMINAL_PROCESSING_STATUSES.has(processingStatus as string);
}

/**
 * The cadence a single still-processing document warrants: fast while it was
 * touched recently, slow until the age cap, `0` (not worth polling for) after.
 * A document with no usable timestamp is treated as just-touched.
 */
function documentPollingInterval(document: PollableDocumentInventoryItem, now: number): number {
  const stamp = document.document_reference.updated_at ?? document.document_reference.created_at;
  if (!stamp) return DOCUMENT_PROCESSING_POLL_INTERVAL_MS;
  const touchedAt = Date.parse(stamp);
  if (Number.isNaN(touchedAt)) return DOCUMENT_PROCESSING_POLL_INTERVAL_MS;
  const age = now - touchedAt;
  if (age < DOCUMENT_PROCESSING_POLL_FAST_WINDOW_MS) return DOCUMENT_PROCESSING_POLL_INTERVAL_MS;
  if (age <= DOCUMENT_PROCESSING_POLL_MAX_AGE_MS) return DOCUMENT_PROCESSING_POLL_SLOW_INTERVAL_MS;
  return 0;
}

/**
 * The RTK Query `pollingInterval` for the document inventory: the cadence the
 * most urgent still-processing document needs — fast for the first
 * `DOCUMENT_PROCESSING_POLL_FAST_WINDOW_MS` after it was touched, slow until
 * the age cap — or `0` (no polling) once everything is settled or past the cap.
 *
 * The function is stateless: callers pass `now`, so no component has to hold a
 * backoff timer. The three consumers re-run this on every fulfilled fetch, so a
 * document crossing the fast-window boundary is picked up at most one 3 s tick
 * late, and one crossing the age cap at most one 10 s tick late. That is why
 * none of them needs a separate timer.
 */
export function documentInventoryPollingInterval(
  documents: ReadonlyArray<PollableDocumentInventoryItem> | undefined,
  pendingUploads: ReadonlyArray<PollablePendingUpload> = [],
  now: number = Date.now(),
  { streamConnected = false }: { streamConnected?: boolean } = {},
): number {
  const persisted = documents ?? [];
  const persistedArtifactIds = new Set(
    persisted.flatMap(document => {
      const artifactId = document.document_reference.source_artifact_id;
      return artifactId ? [artifactId] : [];
    }),
  );
  const candidateIntervals = persisted.flatMap(document => {
    if (isDocumentProcessingComplete(document.document_reference.processing_status)) return [];
    const interval = documentPollingInterval(document, now);
    return interval > 0 ? [interval] : [];
  });
  // An optimistic upload has no server timestamp yet, and resolves within
  // seconds of the next inventory refetch — always treat it as just-touched.
  const uploadStillPending = pendingUploads.some(
    pending =>
      !persistedArtifactIds.has(pending.artifactId) &&
      !isDocumentProcessingComplete(pending.status),
  );
  if (uploadStillPending) candidateIntervals.push(DOCUMENT_PROCESSING_POLL_INTERVAL_MS);
  if (candidateIntervals.length === 0) return 0;
  // Still bounded by the age cap: a document past it contributes no candidate.
  if (streamConnected) return DOCUMENT_PROCESSING_POLL_STREAM_CONNECTED_INTERVAL_MS;
  return Math.min(...candidateIntervals);
}
